/**
 * Warranty Claim Section Component
 * SRV-FG-3: Garanciális igény kezelés szekció a munkalap részletek oldalon
 *
 * Only shown for GARANCIALIS type worksheets.
 */

import {
  getWorksheetWarrantyClaim,
  submitWarrantyClaim,
  type WarrantyClaimInfo,
} from '@/api/worksheets';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

const CLAIM_STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  PENDING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300',
  SUBMITTED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
  UNDER_REVIEW: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
  APPROVED: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
  REJECTED: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
  SETTLED: 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300',
  CANCELLED: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

interface WarrantyClaimSectionProps {
  worksheetId: string;
  deviceName: string;
}

export function WarrantyClaimSection({ worksheetId, deviceName }: WarrantyClaimSectionProps) {
  const { t } = useTranslation();
  const [claim, setClaim] = useState<WarrantyClaimInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [manufacturer, setManufacturer] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fetchClaim = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getWorksheetWarrantyClaim(worksheetId);
      setClaim(response.data);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, [worksheetId]);

  useEffect(() => {
    void fetchClaim();
  }, [fetchClaim]);

  // Auto-detect manufacturer from device name
  useEffect(() => {
    if (!manufacturer && deviceName) {
      const firstWord = deviceName.split(' ')[0]?.toUpperCase() ?? '';
      const knownBrands = [
        'MAKITA',
        'STIHL',
        'HUSQVARNA',
        'BOSCH',
        'DEWALT',
        'MILWAUKEE',
        'HIKOKI',
      ];
      if (knownBrands.includes(firstWord)) {
        setManufacturer(firstWord);
      }
    }
  }, [deviceName, manufacturer]);

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const response = await submitWarrantyClaim(worksheetId, {
        manufacturer: manufacturer || undefined,
        notes: notes || undefined,
      });
      setClaim(response.data);
      setShowSubmitForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-purple-200 dark:border-purple-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <svg
            className="h-5 w-5 text-purple-600 dark:text-purple-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          {t('service.warrantyClaim')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {claim ? (
          <div className="space-y-3">
            {/* Claim status */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {t('service.warrantyClaimNumber')}
              </span>
              <span className="font-medium font-mono">{claim.claimNumber}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('common.status')}</span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${CLAIM_STATUS_COLORS[claim.status] ?? CLAIM_STATUS_COLORS['DRAFT']}`}
              >
                {t(`service.warrantyClaimStatus.${claim.status.toLowerCase()}`)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {t('service.warrantyManufacturer')}
              </span>
              <span className="font-medium">{claim.manufacturer}</span>
            </div>
            {claim.totalClaimValue != null && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('service.warrantyClaimValue')}
                </span>
                <span className="font-medium">{formatCurrency(claim.totalClaimValue)}</span>
              </div>
            )}
            {claim.settledAmount != null && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('service.warrantySettledAmount')}
                </span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {formatCurrency(claim.settledAmount)}
                </span>
              </div>
            )}
            {claim.submittedAt && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('service.warrantySubmittedAt')}
                </span>
                <span>{formatDate(claim.submittedAt)}</span>
              </div>
            )}
            {claim.notes && (
              <div className="mt-2 text-sm text-muted-foreground bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                {claim.notes}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('service.warrantyNoClaimYet')}</p>

            {showSubmitForm ? (
              <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-4 space-y-3">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">
                    {t('service.warrantyManufacturer')}
                  </label>
                  <Input
                    value={manufacturer}
                    onChange={e => setManufacturer(e.target.value)}
                    placeholder={t('service.warrantyManufacturerPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">
                    {t('service.warrantyNotes')}
                  </label>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder={t('service.warrantyNotesPlaceholder')}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background min-h-[80px]"
                  />
                </div>
                {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? t('common.saving') : t('service.warrantySubmitClaim')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowSubmitForm(false);
                      setError(null);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-400 dark:hover:bg-purple-950/20"
                onClick={() => setShowSubmitForm(true)}
              >
                {t('service.warrantyCreateClaim')}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
